import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../../helpers'
import { IKnowledgeArea } from '../../../../interfaces/KnowledgeArea/KnowledgeArea'
import { KnowledgeAreaService } from '../../../../services/KnowledgeArea/KnowledgeAreaServices'
import { KnowledgeAreaFormModal } from './KnowledgeAreaModal'

export default function KnowledgeAreaPage() {
  const service = new KnowledgeAreaService()
  const toast = useAppToast()
  const [knowledgeArea, setKnowledgeArea] = useState<IKnowledgeArea[]>([])
  const [search, setSearch] = useState('')
  const [filtered, setFiltered] = useState<IKnowledgeArea[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<IKnowledgeArea | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<IKnowledgeArea | null>(null)
  const hasRole = useHasRole()
  const permissions = {
    addCourseFilter: hasRole('add_course_filter'),
    deleteCourseFilter: hasRole('delete_course_filter')
  }

  useEffect(() => {
    loadKnowledgeArea()
  }, [])

  const loadKnowledgeArea = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await service.list()
      setKnowledgeArea(response)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as áreas de conhecimento', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleting) return

    try {
      await service.delete(deleting.id)

      toast.success('Área excluída', `A área "${deleting.name}" foi excluída com sucesso.`)

      setIsDeleteOpen(false)
      setDeleting(null)

      await loadKnowledgeArea()
    } catch (error) {
      toast.error('Erro ao excluir', 'Não foi possível excluir a área. Tente novamente.')
    }
  }

  useEffect(() => {
    setFiltered(knowledgeArea.filter((area) => area.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, knowledgeArea])

  return (
    <>
      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por área"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="áreas" feminine onRetry={loadKnowledgeArea} />
            ) : filtered.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="área" feminine />
            ) : (
              filtered.map((area) => (
                <Tr key={area.id}>
                  <Td>{area.id}</Td>
                  <Td>{area.name}</Td>
                  <Td>{formatDateTime(area.created_at)}</Td>
                  <Td>
                    {permissions.deleteCourseFilter && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setEditing(area)
                          setIsFormOpen(true)
                        }}
                      />
                    )}
                    {permissions.deleteCourseFilter && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeleting(area)
                          setIsDeleteOpen(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <KnowledgeAreaFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        editing={editing}
        onSave={async (name: string, id?: number) => {
          if (id) {
            await service.update(id, { name })
          } else {
            await service.create({ name })
          }
          await loadKnowledgeArea()
        }}
      />
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        action="delete"
        entity="área de conhecimento"
        feminine
        name={deleting?.name}
      />

      {permissions.addCourseFilter && (
        <ActionFab
          label="Adicionar área de conhecimento"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        />
      )}
    </>
  )
}
